import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, ArrowRight, Save, Upload, X, Package, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const CreateCampaign = () => {
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: Campaign Basics
    campaign_name: '',
    ad_type: 'sponsored_listing',
    
    // Step 2: Budget & Schedule
    total_budget: '',
    daily_budget: '',
    start_date: '',
    end_date: '',
    
    // Step 3: Targeting
    target_location: 'all',
    target_category: '',
    
    // Step 4: Creative
    headline: '',
    description: '',
    cta_text: 'Learn More',
    ad_images: [],
    
    // Step 5: Product Selection (for product ads)
    selected_products: []
  });

  const totalSteps = 5;

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      const response = await apiCall('/api/listings/');
      const vendorProducts = response.filter(
        listing => listing.listing_type === 'product' || listing.listing_type === 'rental'
      );
      setProducts(vendorProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate file size and type
    const maxSize = 5 * 1024 * 1024; // 5MB
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    
    for (const file of files) {
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 5MB`);
        return;
      }
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name} is not a supported format. Use JPG, PNG, or WebP`);
        return;
      }
    }

    try {
      setUploadingImages(true);
      toast.info(`Uploading ${files.length} image(s)...`);
      
      const uploadPromises = files.map(async (file) => {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        const response = await apiCall('/api/files/upload', {
          method: 'POST',
          body: uploadFormData,
          headers: {}
        });

        return response.url;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      setFormData(prev => ({
        ...prev,
        ad_images: [...prev.ad_images, ...uploadedUrls]
      }));
      toast.success(`${uploadedUrls.length} image(s) uploaded successfully`);
    } catch (error) {
      toast.error('Failed to upload images');
      console.error(error);
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      ad_images: prev.ad_images.filter((_, i) => i !== index)
    }));
    toast.success('Image removed');
  };

  const toggleProductSelection = (productId) => {
    setFormData(prev => {
      const isSelected = prev.selected_products.includes(productId);
      return {
        ...prev,
        selected_products: isSelected
          ? prev.selected_products.filter(id => id !== productId)
          : [...prev.selected_products, productId]
      };
    });
  };

  const handleNext = () => {
    // Validation
    if (step === 1 && !formData.campaign_name) {
      toast.error('Please enter a campaign name');
      return;
    }
    if (step === 2 && (!formData.total_budget || !formData.daily_budget || !formData.start_date || !formData.end_date)) {
      toast.error('Please fill in all budget and schedule fields');
      return;
    }
    if (step === 4) {
      if (!formData.headline || !formData.description) {
        toast.error('Please fill in headline and description');
        return;
      }
      // Make image mandatory for display ads
      if (formData.ad_type === 'display_ad' && formData.ad_images.length === 0) {
        toast.error('Display ads require at least one image');
        return;
      }
    }
    
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      const payload = {
        campaign_name: formData.campaign_name,
        ad_type: formData.ad_type,
        total_budget: parseFloat(formData.total_budget),
        daily_budget: parseFloat(formData.daily_budget),
        start_date: formData.start_date,
        end_date: formData.end_date,
        targeting: {
          location: formData.target_location,
          category: formData.target_category
        },
        creative: {
          headline: formData.headline,
          description: formData.description,
          cta_text: formData.cta_text,
          images: formData.ad_images
        },
        product_ids: formData.selected_products
      };
      
      await apiCall('/api/marketing/campaigns', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      toast.success('Campaign created successfully! Pending admin approval.');
      navigate('/vendor/advertising');
    } catch (error) {
      toast.error(error.message || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Campaign Basics</h2>
            <div>
              <Label>Campaign Name *</Label>
              <Input
                value={formData.campaign_name}
                onChange={(e) => setFormData({...formData, campaign_name: e.target.value})}
                placeholder="e.g., Summer Sale Campaign"
                required
              />
            </div>
            <div>
              <Label>Ad Type *</Label>
              <Select value={formData.ad_type} onValueChange={(value) => setFormData({...formData, ad_type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sponsored_listing">Sponsored Listing</SelectItem>
                  <SelectItem value="display_ad">Display Ad</SelectItem>
                  <SelectItem value="search_ad">Search Ad</SelectItem>
                  <SelectItem value="product_carousel">Product Carousel</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Budget & Schedule</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Total Budget (₹) *</Label>
                <Input
                  type="number"
                  value={formData.total_budget}
                  onChange={(e) => setFormData({...formData, total_budget: e.target.value})}
                  placeholder="10000"
                  required
                />
              </div>
              <div>
                <Label>Daily Budget (₹) *</Label>
                <Input
                  type="number"
                  value={formData.daily_budget}
                  onChange={(e) => setFormData({...formData, daily_budget: e.target.value})}
                  placeholder="500"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  required
                />
              </div>
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Targeting</h2>
            <div>
              <Label>Target Location</Label>
              <Select value={formData.target_location} onValueChange={(value) => setFormData({...formData, target_location: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  <SelectItem value="local">Local Only</SelectItem>
                  <SelectItem value="regional">Regional</SelectItem>
                  <SelectItem value="national">National</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Target Category (Optional)</Label>
              <Input
                value={formData.target_category}
                onChange={(e) => setFormData({...formData, target_category: e.target.value})}
                placeholder="e.g., Electronics"
              />
            </div>
          </div>
        );
      
      case 4:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Ad Creative</h2>
            <div>
              <Label>Headline *</Label>
              <Input
                value={formData.headline}
                onChange={(e) => setFormData({...formData, headline: e.target.value})}
                placeholder="Grab attention with a compelling headline"
                maxLength={60}
                required
              />
              <p className="text-xs text-gray-500 mt-1">{formData.headline.length}/60 characters</p>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe your offer..."
                rows={4}
                maxLength={150}
                required
              />
              <p className="text-xs text-gray-500 mt-1">{formData.description.length}/150 characters</p>
            </div>
            <div>
              <Label>Call to Action</Label>
              <Select value={formData.cta_text} onValueChange={(value) => setFormData({...formData, cta_text: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Learn More">Learn More</SelectItem>
                  <SelectItem value="Shop Now">Shop Now</SelectItem>
                  <SelectItem value="Get Offer">Get Offer</SelectItem>
                  <SelectItem value="View Details">View Details</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>
                  Ad Images {formData.ad_type === 'display_ad' ? '*' : '(Optional)'}
                </Label>
                {formData.ad_type === 'display_ad' && (
                  <div className="flex items-center gap-1 text-xs text-orange-600">
                    <AlertCircle className="h-3 w-3" />
                    <span>Required for display ads</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-2">Recommended: 1200x628px for best results</p>
              <div className="space-y-3">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer">
                  <Input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                    disabled={uploadingImages}
                  />
                  <label htmlFor="image-upload" className="cursor-pointer block">
                    <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm font-medium text-gray-600">
                      {uploadingImages ? 'Uploading...' : 'Click to upload ad images'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG, WebP (Max 5MB each)</p>
                    <p className="text-xs text-blue-600 mt-1">Recommended size: 1200x628px</p>
                  </label>
                </div>
                
                {formData.ad_images.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
                        {formData.ad_images.length} uploaded
                      </span>
                      Uploaded Images
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {formData.ad_images.map((url, index) => (
                        <div key={url.id || url.name || `${url.title || "url"}-${index}`} className="relative group">
                          <img
                            src={url}
                            alt={`Ad ${index + 1}`}
                            className="w-full h-40 object-cover rounded-lg border-2 border-gray-200"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="bg-red-500 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            >
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                          <div className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                            Image {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      
      case 5:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Select Products for Campaign</h2>
            <p className="text-sm text-gray-600">Choose which products to promote in this campaign</p>
            
            {loadingProducts ? (
              <p className="text-center py-8 text-gray-500">Loading products...</p>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No products available</p>
                <p className="text-sm text-gray-400 mt-2">Create products first to use them in campaigns</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <Checkbox
                      checked={formData.selected_products.includes(product.id)}
                      onCheckedChange={() => toggleProductSelection(product.id)}
                    />
                    {product.media && product.media[0] ? (
                      <img
                        src={product.media[0]}
                        alt={product.title}
                        className="w-16 h-16 object-cover rounded"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                        <Package className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium">{product.title}</p>
                      <p className="text-sm text-gray-500">{product.category}</p>
                      {product.data?.price && (
                        <p className="text-sm font-semibold text-green-600">₹{product.data.price}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-4">
              <p className="text-sm font-medium text-blue-800 mb-2">
                Selected Products: {formData.selected_products.length}
              </p>
              <p className="text-xs text-blue-700">
                {formData.selected_products.length === 0 
                  ? 'You can submit without selecting products, or select products to feature them in your campaign.'
                  : 'These products will be featured in your campaign ads.'}
              </p>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/vendor/advertising')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Create Campaign</h1>
            <p className="text-gray-600">Step {step} of {totalSteps}</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>

        {/* Step Content */}
        <Card>
          <CardContent className="pt-6">
            {renderStep()}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={step === 1}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
          
          {step < totalSteps ? (
            <Button onClick={handleNext}>
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading}>
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Submitting...' : 'Submit for Approval'}
            </Button>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CreateCampaign;